/**
 * 관리자 매칭리스트 문자발송 ($j.Pages.Admin.MatchList / body data-page-name="MatchList")
 *
 * blade 는 DATA 주입(#page-data)과 DOM 컨테이너만 두고,
 * 렌더/상태/이벤트 로직 일체는 이 모듈에서 담당한다.
 *
 * 모듈 구성:
 *   MatchList.js                       — 메인 클래스 (이 파일)
 *   MatchList/MatchListUtils.js     — 상수 + 순수 유틸
 *   MatchList/SendModal.js             — 발송 모달 / 템플릿 모달 / 미리보기
 *
 * 데이터: matchListController 가 주입한 #page-data (JSON)
 *   { order, matchType, orderInfoRows, internalMembers, externalAgencies,
 *     internalTemplates, externalTemplates, sendHistory }
 */
import {
    EQUIPMENT_TYPES, DISTANCE_BUCKETS, INACTIVE_PERIODS, WORK_PERIODS,
    ACCESS_GROUPS, accessGroupFor, PAID_GROUPS, paidGroupOf,
    initDataTable, daysSince, kmLabel,
    distanceBucketFor, periodsUpTo, maxValue, isRestricted,
    shortAddress, shortDateTime, escapeHtml, msgTypeBadge,
} from './MatchList/MatchListUtils';
import SendModal           from './MatchList/SendModal';
import MatchListTables  from './MatchList/MatchListTables';
import Util from '../../Common/Util';

export default class MatchList {
    /**
     * app.js 의 loadPageModule 이 DOM ready 후에 호출.
     * 1) 서버 주입 데이터를 인스턴스 필드로 흡수
     * 2) 상태 초기화
     * 3) 전역 브리지 → 초기 렌더 → 이벤트 바인딩
     */
    /** 서버 데이터 (#page-data) — 한 번 읽어 둔다 */
    _data() {
        return this.__data || (this.__data = JSON.parse(document.getElementById('page-data')?.textContent || '{}'));
    }

    initialize() {
        const DATA = this._data();
        this.DATA = DATA;

        // ── 서버 주입 데이터 (orderInfoRows / order / 회원·업체 목록 / 템플릿 / 발송이력) ──
        this.ORDER_INFO_ROWS = DATA.orderInfoRows || [];

        this.ORDER = {
            lat:            DATA.order?.lat            ?? 0,
            lng:            DATA.order?.lng            ?? 0,
            equipmentKey:   DATA.order?.equipmentKey   ?? 'excavator',
            equipmentType:  DATA.order?.equipmentType  ?? '',
            equipmentLabel: DATA.order?.equipmentLabel ?? '',
        };

        this.PLACEHOLDERS = {
            기종:     [DATA.order?.equipmentType, DATA.order?.equipmentLabel].filter(Boolean).join(' '),
            옵션:     DATA.order?.equipmentOption ?? '',  // 장비옵션·전고 (긴급·수수료 제외). 없으면 빈칸 — {기종} 과 같은 줄에 쓰므로 줄을 지우지 않는다
            수수료:   DATA.order?.feeLabel       ?? '',   // '수수료없음' 만 표기, 수수료 있으면 빈칸 (발주 기준)
            작업일시: DATA.order?.workDateTime   ?? '',
            소요시간: DATA.order?.duration       ?? '',
            현장주소: DATA.order?.address        ?? '',   // 발송 시 PLACEHOLDERS_SHORT 에서 동/읍/면까지 축약
            긴주소:   DATA.order?.address        ?? '',   // site_address 원문(풀) — 축약 안 함. 외부배차 현정보 템플릿 전용
            예상금액: DATA.order?.estimate       ?? '협의',
            금액:     DATA.order?.estimate       ?? '협의',   // {예상금액} 별칭
            발주자:   DATA.order?.ordererName    ?? '',
            '발주자 연락처': DATA.order?.ordererPhone ?? '',
            '현장번호':      DATA.order?.sitePhone    ?? '',   // 접수페이지 현장번호(site_phone). 비면 expandPlaceholders 가 해당 줄 제거
        };
        this.PLACEHOLDERS_SHORT = {
            ...this.PLACEHOLDERS,
            현장주소: shortAddress(this.PLACEHOLDERS.현장주소),
            작업일시: shortDateTime(this.PLACEHOLDERS.작업일시),
        };

        this.internalMembers      = DATA.internalMembers  || [];
        // 외부배차 — 서버에서 발주 car_type 매칭 + 거리 계산 + 거리 오름차순 정렬 완료
        this.externalAgencies     = DATA.externalAgencies || [];
        this.externalWithDistance = this.externalAgencies;

        // 외부배차(94) 전환 오더 — 서버가 외부 탭에 배차된 1명만 내려줌. 내부 탭 숨김 + 외부 탭 기본.
        this.isExtDispatch = !!(DATA.order && DATA.order.isExternalDispatch);

        this.responseList = [
            { rank:1, source:'외부', name:'대한중기 (이○○)', distance: 0, equipment:'굴삭기 중형 03W', clickTime:'15:12:18', messageType:'RCS', status:'대기' },
            { rank:2, source:'외부', name:'청주굴삭 (나○○)', distance:37, equipment:'굴삭기 중형 03W', clickTime:'15:14:02', messageType:'RCS', status:'대기' },
            { rank:3, source:'외부', name:'세종건기 (장○○)', distance:68, equipment:'굴삭기 중형 03W', clickTime:'15:16:45', messageType:'SMS', status:'대기' },
        ];

        // sendHistory — DB 발송내역 (g5_sms_send_orders ⋈ g5_sms_send_logs(purpose='match'), 현재 오더 기준)
        this.sendHistory = DATA.sendHistory || [];

        // 템플릿 — g5_sms_template purpose='match' 에서 서버 로드
        this.internalTemplates = DATA.internalTemplates || [];
        this.externalTemplates = DATA.externalTemplates || [];

        // ── 화면 상태 ──
        this.state = {
            sendMode: false,
            historyMode: false,   // 발송내역 전용 보기 (발송 가능 여부와 무관)
            showSettings: false,
            activeTab: 'internal',  // 외부배차여도 내부매칭 리스트가 기본 (지정자는 상단 빨간 행으로 표시)
            sortByInactive: '',
            showBizDistEditor: false,
            showInactiveEditor: false,
            selectedIntDist: new Set(),
            selectedExtDist: new Set(),
            selectedAccess: new Set(),   // '접속'/'미접속' 그룹 선택 (오늘 접속 여부)
            selectedPaid: new Set(),     // 'paid'/'free' 그룹 선택 (유료/무료 회원)
            selectedExtWorkPeriods: new Set(),  // 외부배차 최근오더일 선택
            checkedInternal: new Set(),
            checkedExternal: new Set(),
            distanceConfig: { sky: 30, ladder: 20, crane: 50, excavator: 30, forklift: 10, lift: 20 },
            editingBiz: '',
            editingKm: null,
            inactiveConfigPeriods: periodsUpTo(30),
            historyFilter: 'all',
            historySearch: '',
        };

        this.smsState = {
            excludeSent: false,
            intTplIdx: 0,
            extTplIdx: 0,
            showPreview: false,
            smsIntText: '',
            smsExtText: '',
            senderIdx: 0,            // senderPhones 인덱스 — 등록된 번호 중에서만 선택
        };

        // 발송이력 DataTables 런타임
        this.historyDt = null;
        this.historyFiltered = [];

        // 별도 모듈로 분리된 영역
        this.sendModal = new SendModal(this);          // 발송 모달 / 템플릿 모달 / 미리보기
        this.tables    = new MatchListTables(this); // 내부 / 외부 / 응답 테이블

        // 리로드 후 탭 복원 — 발송 후엔 'history'(발송내역), 외부회원 편집 저장 후엔 'external'(외부배차) 등.
        const savedTab = $j.Common.Store.session.get('matchList.activeTab');
        if (savedTab) {
            $j.Common.Store.session.remove('matchList.activeTab');
            if (savedTab === 'history') this.state.historyMode = true;
            else this.state.activeTab = savedTab;
        }
        // 발송모드 복원 — 외부회원 편집 저장 후엔 '문자발송 선택된' 화면으로 재진입(enterSendMode 와 동일).
        if ($j.Common.Store.session.get('matchList.sendMode') === '1') {
            $j.Common.Store.session.remove('matchList.sendMode');
            if (DATA.order && DATA.order.canSend) {
                this.state.sendMode = true;
                // 외부배차(전환) 오더는 배차된 1명만 내려오므로 발송대상 자동 체크 → 바로 발송 가능
                if (this.isExtDispatch) this.state.checkedExternal = new Set(this.externalListSendable().map(e => e.id));
            }
        }

        this.exposeGlobals();
        this.renderOrderInfo();
        this.bindEvents();
        this.render();
    }

    // ============================================================
    //  전역 브리지 — 인라인 onclick 호환
    // ============================================================
    exposeGlobals() {
        const self = this;
        // 통계 모달 — renderOrderInfo() 가 만든 <button onclick="openMatchStat(num)">
        // 단계분포 모달 — <button onclick="openMatchLevelChart()">
        window.openMatchStat = (ordernum) => self.openMatchStat(ordernum);
        window.openMatchLevelChart = () => self.openMatchLevelChart();
    }

    // 통계 모달 — matchList 의 log_popup 과 동일한 JBModal 호출
    openMatchStat(ordernum) {
        $j.Ui.JBModal.target('match-percent-modal', {
            target: '$j.F.Admin.Order.MatchPercent',
            data:   { ordernum },
            title:  '통계',
            width:  '1000px',
        });
    }

    // 단계분포 모달 — 매칭된 회원의 단계(lvl)별 인원수 (NC ver=4 는 -6차 포인트 등급별)
    openMatchLevelChart() {
        $j.Ui.JBModal.target('match-level-chart-modal', {
            target: '$j.F.Admin.Order.MatchLevelChart',
            data:   {
                stats:     this.DATA.levelStats || [],
                levelAll:  this.DATA.levelAll  || [],
                matchType: this.DATA.matchType || '',
                ver:       this.DATA.matchVer  || '',
            },
            title:  '단계분포',
            width:  '980px',
        });
    }

    // ============================================================
    //  발송번호 / 거리 버킷 헬퍼
    // ============================================================
    // g5_config.sms_sender_phones (콤마구분자) — raw 로 주입. 표시 시 phoneNumber() 적용
    senderPhones() {
        return this._senderPhones || (this._senderPhones = this._data().senderPhones || []);
    }
    currentSenderNum() {
        return this.senderPhones()[this.smsState.senderIdx] || '';
    }
    currentBuckets() { return this.bucketsFor(this.ORDER.equipmentKey); }
    bucketsFor(key) { return DISTANCE_BUCKETS[key] || [5, 10, 15, 20, 30, 50, 100, Infinity]; }

    // ============================================================
    //  비즈니스 로직 (필터·모드 전환)
    // ============================================================
    // 외부배차 — 서버에서 이미 발주 car_type 매칭만 추려 정렬되어 옴
    externalListFiltered() { return this.externalWithDistance; }
    externalListSendable() { return this.externalWithDistance.filter(e => e.notifyOk); }

    // 내부 매칭 필터 차원별 판정 — 해당 차원 미선택이면 통과(true).
    matchDist(m) {
        const s = this.state.selectedIntDist;
        if (s.size === 0) return true;
        const b = distanceBucketFor(m.distance, this.currentBuckets());
        return b !== null && s.has(b);
    }
    matchAccess(m) {
        const s = this.state.selectedAccess;
        return s.size === 0 || s.has(accessGroupFor(daysSince(m.lastAccess)));
    }
    matchPaid(m) {
        const s = this.state.selectedPaid;
        return s.size === 0 || s.has(paidGroupOf(m));
    }

    // 내부 매칭 필터 — 거리 AND 접속 AND 유료/무료 (교집합).
    //   각 차원 미선택이면 그 조건은 통과. 셋 다 미선택이면 체크 0명.
    applyFilters() {
        const { state } = this;
        if (state.selectedIntDist.size === 0 && state.selectedAccess.size === 0 && state.selectedPaid.size === 0) {
            state.checkedInternal = new Set();
            return;
        }
        // 수발주제한 회원은 일괄 자동선택에서 제외 (개별 클릭은 가능)
        const result = this.internalMembers.filter(m =>
            !isRestricted(m) && this.matchDist(m) && this.matchAccess(m) && this.matchPaid(m)
        );
        state.checkedInternal = new Set(result.map(m => m.no));
    }

    // 외부배차 최근오더일 버킷 — (prev, this] 일 범위 (예: 3개월 버튼은 days∈(30, 90]).
    workdayBucketFor(days) {
        if (!Number.isFinite(days)) return null;
        const periods = WORK_PERIODS.map(p => p.value);
        for (let i = 0; i < periods.length; i++) {
            const lo = i === 0 ? -1 : periods[i - 1];
            if (days > lo && days <= periods[i]) return periods[i];
        }
        return null;
    }

    // 외부 필터 — 거리 버킷 AND 최근오더일 버킷 (교집합). 내부 applyFilters 와 동일 패턴.
    applyExtFilters() {
        const { state } = this;
        if (state.selectedExtDist.size === 0 && state.selectedExtWorkPeriods.size === 0) {
            state.checkedExternal = new Set();
            return;
        }
        const buckets = this.currentBuckets();
        const result = this.externalListSendable().filter(e => {
            if (state.selectedExtDist.size > 0) {
                const db = distanceBucketFor(e.distance, buckets);
                if (db === null || !state.selectedExtDist.has(db)) return false;
            }
            if (state.selectedExtWorkPeriods.size > 0) {
                const wb = this.workdayBucketFor(daysSince(e.workDay));
                if (wb === null || !state.selectedExtWorkPeriods.has(wb)) return false;
            }
            return true;
        });
        state.checkedExternal = new Set(result.map(e => e.id));
    }

    enterSendMode() {
        this.state.sendMode = true;
        this.state.selectedIntDist = new Set();
        this.state.selectedExtDist = new Set();
        this.state.checkedInternal = new Set();
        this.state.checkedExternal = new Set();
        this.state.selectedAccess = new Set();
        this.state.selectedPaid = new Set();
        this.state.selectedExtWorkPeriods = new Set();
        // 배차후(외부배차 전환) 는 배차된 1명만 내려오므로 발송대상 자동 체크 → 바로 발송 가능
        // (수신거부 건은 externalListSendable 에서 제외되어 체크되지 않음)
        if (this.isExtDispatch) {
            this.state.checkedExternal = new Set(this.externalListSendable().map(e => e.id));
        }
        this.render();
    }

    exitSendMode() {
        Object.assign(this.state, {
            sendMode: false,
            showSettings: false,
            showBizDistEditor: false,
            showInactiveEditor: false,
            selectedIntDist: new Set(),
            selectedExtDist: new Set(),
            checkedInternal: new Set(),
            checkedExternal: new Set(),
            selectedAccess: new Set(),
            selectedPaid: new Set(),
            selectedExtWorkPeriods: new Set(),
            // activeTab 은 그대로 둔다 — 탭은 발송모드 밖에서도 보이므로 보던 탭에 머문다
            sortByInactive: '',
            editingBiz: '',
            editingKm: null,
            historyFilter: 'all',
            historySearch: '',
        });
        this.render();
    }

    // 발송내역 보기 — 수주대기가 아니어도(=문자발송 불가) 내역 조회 가능
    enterHistoryMode() {
        this.state.historyMode = true;
        this.render();
    }

    exitHistoryMode() {
        this.state.historyMode = false;
        this.render();
    }

    toggleInactiveDay(value) {
        const max = maxValue([...this.state.inactiveConfigPeriods]);
        if (value > max) {
            this.state.inactiveConfigPeriods = periodsUpTo(value);
        } else if (value === max) {
            const below = INACTIVE_PERIODS.filter(p => p.value < value);
            this.state.inactiveConfigPeriods = below.length
                ? periodsUpTo(Math.max(...below.map(p => p.value)))
                : [];
        } else {
            const candidates = INACTIVE_PERIODS.filter(p => p.value < value && this.state.inactiveConfigPeriods.includes(p.value));
            this.state.inactiveConfigPeriods = candidates.length
                ? periodsUpTo(Math.max(...candidates.map(p => p.value)))
                : [];
        }
        this.render();
    }

    // ============================================================
    //  렌더 — 상단 영역
    // ============================================================
    renderOrderInfo() {
        const matchType = this.DATA.matchType || '';
        const showStat  = ['NC', 'NS', 'NS2', 'NE'].includes(matchType);   // 당일외 계열만 매칭 통계가 의미 있다
        const orderNum  = this.DATA.order?.num || 0;
        const html = this.ORDER_INFO_ROWS
            .map(([label, value]) => {
                if (label !== '유형') {
                    return `<div class="it"><span class="lb">${label}</span><span>${value}</span></div>`;
                }
                // 유형: 한글명만으로는 어느 세대(ver)로 매칭됐는지 알 수 없다 →
                // 배분분포·통계모달과 같은 표기($j.F.Admin.MatchTag)를 앞에 붙인다.
                const btns = showStat
                    ? `<button type="button" class="oib is-stat" onclick="openMatchStat(${orderNum})">통계</button>`
                      + `<button type="button" class="oib is-lvl" onclick="openMatchLevelChart()">단계분포</button>`
                    : '';
                return `<div class="it"><span class="lb">${label}</span>
                    <span class="oi-type">${$j.F.Admin.MatchTag.badge(matchType, this.DATA.matchInfoVer)}${value}${btns}</span>
                </div>`;
            })
            .join('');
        $('#oI').html(html);
    }

    renderEquipmentGrid(selector) {
        const html = EQUIPMENT_TYPES.map(t => `
            <div class="c${t.key === this.ORDER.equipmentKey ? ' hl' : ''}">
                <span class="l">${t.label}${t.key === this.ORDER.equipmentKey ? ' ★' : ''}</span>
                <span class="v">${kmLabel(this.state.distanceConfig[t.key], this.bucketsFor(t.key))}</span>
            </div>`).join('');
        $(selector).html(html);
    }

    renderBizDistEditor(selector) {
        const { editingBiz, editingKm, distanceConfig } = this.state;
        let html = '<select class="bsel"><option value="">업종 선택</option>'
                 + EQUIPMENT_TYPES.map(t =>
                       `<option value="${t.key}"${editingBiz === t.key ? ' selected' : ''}>${t.label} (${kmLabel(distanceConfig[t.key], this.bucketsFor(t.key))})</option>`
                   ).join('')
                 + '</select>';

        if (editingBiz) {
            const opts = this.bucketsFor(editingBiz);
            html += '<div style="display:flex;gap:4px;flex-wrap:wrap">'
                  + opts.map(k => `<button class="pl${editingKm != null && k <= editingKm ? ' on' : ''}" data-bkm="${k}">${kmLabel(k, opts)}</button>`).join('')
                  + '</div>';
            const changed = editingKm != null && editingKm !== distanceConfig[editingBiz];
            const bg = changed ? '#1D4ED8' : '#15803D';
            html += `<button class="bc2" style="padding:5px 14px;border-radius:8px;border:none;font-size:12px;font-weight:700;color:#fff;background:${bg}">${changed ? '수정' : '확인'}</button>`;
        }
        $(selector).html(html);
    }

    renderInactiveConfigPills() {
        const html = INACTIVE_PERIODS.map(p =>
            `<button class="pr${this.state.inactiveConfigPeriods.includes(p.value) ? ' on' : ''}" data-iv="${p.value}">${p.label}</button>`
        ).join('');
        $('#iPs').html(html);
    }

    renderLegend() {
        let html = '';
        if (this.state.activeTab === 'internal') {
            html = `
                <span style="display:flex;align-items:center;gap:3px"><span class="ld2" style="background:#FF7E7E;border:1px solid #FF7E7E"></span>수주자</span>
                <span style="display:flex;align-items:center;gap:3px"><span class="ld2" style="background:#EFF6FF;border:1px solid #3B82F6"></span>선택</span>
                <span style="display:flex;align-items:center;gap:3px">차수/차수설명 셀: 차수별 그룹 색</span>`;
        } else if (this.state.activeTab === 'external') {
            html = `
                <span style="display:flex;align-items:center;gap:3px"><span class="ld2" style="background:#EFF6FF;border:1px solid #3B82F6"></span>선택</span>
                <span style="display:flex;align-items:center;gap:3px"><span class="ld2" style="background:#f5f5f5;border:1px solid #ccc"></span>수신거부</span>`;
        }
        $('#lgA').html(html);
    }

    renderDistancePills() {
        const { state } = this;
        const isInternal = state.activeTab === 'internal';
        const selected   = isInternal ? state.selectedIntDist : state.selectedExtDist;
        const checkedCnt = isInternal ? state.checkedInternal.size : state.checkedExternal.size;
        const refuseCnt  = this.externalListFiltered().filter(e => !e.notifyOk).length;
        const buckets    = this.currentBuckets();

        // 접속·유료 필터가 걸려 있으면 그 조건을 통과한 회원만 거리 카운트 후보로 (내부 탭 전용)
        const intCandidates = isInternal
            ? this.internalMembers.filter(m => this.matchAccess(m) && this.matchPaid(m))
            : this.internalMembers;

        let html = '<span style="font-size:12px;font-weight:700;color:#79530C;width:72px;flex-shrink:0;text-align:right;padding-right:10px">거리선택</span>';

        buckets.forEach((km, idx) => {
            const lower  = idx === 0 ? 0 : buckets[idx - 1];
            const isLast = idx === buckets.length - 1;   // 마지막 버킷(Infinity)은 상한 없음 = lower 초과
            const inRange = (d) => d > lower && (isLast || d <= km);
            const count = isInternal
                ? intCandidates.filter(m => inRange(m.distance)).length
                : this.externalListSendable().filter(e => inRange(e.distance)).length;
            const title = isLast ? `${lower}km 초과 (${count}명)` : `${lower}~${km}km (${count}명)`;
            html += `<button class="dp${selected.has(km) ? ' on' : ''}" data-km="${km}" title="${title}">${kmLabel(km, buckets)}</button>`;
        });

        html += `<span style="font-size:12px;color:#79530C;font-weight:700;margin-left:4px">${checkedCnt}명`;
        if (!isInternal && refuseCnt) html += `<span style="color:#909090;font-weight:400;font-size:12px"> (거부${refuseCnt} 제외)</span>`;
        html += '</span>';

        html += selected.size > 0
            ? '<button class="sa-btn sa-clr" id="saToggle">전체취소</button>'
            : '<button class="sa-btn sa-sel" id="saToggle">전체선택</button>';

        $('#dPR').html(html);
    }

    renderInactiveBar() {
        if (this.state.activeTab === 'external') { this.renderExtWorkBar(); return; }
        if (this.state.activeTab !== 'internal') { $('#iBar').hide(); return; }

        // 접속 카운트는 (거리 AND 유료) 통과 회원만, 유료 카운트는 (거리 AND 접속) 통과 회원만 후보로
        const accessCands = this.internalMembers.filter(m => this.matchDist(m) && this.matchPaid(m));
        const paidCands   = this.internalMembers.filter(m => this.matchDist(m) && this.matchAccess(m));

        // ── 접속 선택 셀 (빨강) ──
        let html = '<div class="ib-cell ib-access"><span class="ib-lb is-col">접속 선택</span>';
        ACCESS_GROUPS.forEach(g => {
            // 접속(오늘) / 미접속(그 외) 2분류
            const count = accessCands.filter(m => accessGroupFor(daysSince(m.lastAccess)) === g.value).length;
            html += `<button class="ip${this.state.selectedAccess.has(g.value) ? ' on' : ''}" data-iip="${g.value}">${g.label} (${count})</button>`;
        });
        html += this.state.selectedAccess.size > 0
            ? '<button class="sa-btn sa-clr" id="iToggle">전체취소</button>'
            : '<button class="sa-btn sa-sel" id="iToggle">전체선택</button>';
        html += '</div>';

        // ── 유료/무료 셀 (파랑) ──
        html += '<div class="ib-cell ib-paid"><span class="ib-lb">회원구분</span>';
        PAID_GROUPS.forEach(g => {
            const count = paidCands.filter(m => paidGroupOf(m) === g.value).length;
            html += `<button class="mp${this.state.selectedPaid.has(g.value) ? ' on' : ''}" data-pp="${g.value}">${g.label} (${count})</button>`;
        });
        html += this.state.selectedPaid.size > 0
            ? '<button class="sa-btn sa-clr" id="pToggle">전체취소</button>'
            : '<button class="sa-btn sa-sel" id="pToggle">전체선택</button>';
        html += '</div>';

        $('#iBar').html(html).css('display', 'flex');
    }

    // 외부배차 최근오더일 선택 바 — 미접속 바와 동일 패턴 (거리 필터와 교집합)
    renderExtWorkBar() {
        const { state } = this;
        const buckets = this.currentBuckets();
        // 거리 필터가 걸려 있으면 그 범위 안의 회원만 카운트 후보로
        const candidates = state.selectedExtDist.size > 0
            ? this.externalListSendable().filter(e => {
                const db = distanceBucketFor(e.distance, buckets);
                return db !== null && state.selectedExtDist.has(db);
              })
            : this.externalListSendable();

        let html = '<div class="ib-cell ib-access"><span class="ib-lb is-col">최근오더일</span>';
        WORK_PERIODS.forEach(p => {
            const count = candidates.filter(e => this.workdayBucketFor(daysSince(e.workDay)) === p.value).length;
            html += `<button class="wp${state.selectedExtWorkPeriods.has(p.value) ? ' on' : ''}" data-wp="${p.value}">${p.label} (${count})</button>`;
        });
        html += state.selectedExtWorkPeriods.size > 0
            ? '<button class="sa-btn sa-clr" id="wToggle">전체취소</button>'
            : '<button class="sa-btn sa-sel" id="wToggle">전체선택</button>';
        html += '</div>';

        $('#iBar').html(html).css('display', 'flex');
    }

    // 내부/외부/응답 테이블 → MatchList/MatchListTables.js (this.tables) 로 분리

    // ============================================================
    //  발송이력 — DataTables row.child() 펼치기/접기
    // ============================================================
    filterHistory() {
        const { state } = this;
        return this.sendHistory.filter(rec => {
            if (state.historyFilter === 'today') {
                if (rec.dateTime.replace(/\./g, '-').slice(0, 10) !== new Date().toISOString().slice(0, 10)) return false;
            }
            if (state.historyFilter === 'week') {
                const weekAgo = new Date();
                weekAgo.setDate(weekAgo.getDate() - 7);
                if (new Date(rec.dateTime.replace(/\./g, '-').replace(' ', 'T')) < weekAgo) return false;
            }
            if (state.historySearch) {
                const names = [...rec.internal, ...rec.external].map(r => r.name).join(' ').toLowerCase();
                if (names.indexOf(state.historySearch.toLowerCase()) === -1) return false;
            }
            return true;
        });
    }

    renderHistoryDetail(rec, idx) {
        const renderPerson = (r, j, isInt) => `
            <div class="hd-person">
                <span style="font-size:14px;font-weight:700;color:#909090;width:20px;text-align:center;flex-shrink:0">${j + 1}</span>
                <span class="hd-nm">${isInt && Number(r.no) ? $j.F.Admin.Member.Pop.cell(r.no, r.name, { stop: true }) : r.name}</span>
                <span class="hd-dist">${Util.phoneNumber(r.phone || '')}</span>
                <span>${msgTypeBadge(r.messageType)}</span>
                <span class="hd-st ${r.result === '성공' ? 'hd-ok' : 'hd-fail'}">${r.result}</span>
                ${r.result === '실패' ? `<button class="hd-resend" data-hidx="${idx}" data-htype="${isInt ? 'internal' : 'external'}" data-hri="${j}">재발송</button>` : ''}
            </div>`;
        let html = '<div style="padding:12px 14px 12px 58px;background:#f8f9fa">';
        // 한 배치는 모두 같은 문자내용 — 대표 1건만 내부 목록 위에 표시
        if (rec.message) {
            html += '<div style="margin-bottom:18px">'
                  + '<div style="display:flex;align-items:center;gap:8px;margin-bottom:6px">'
                  + '<span style="font-size:12px;font-weight:700;color:#79530C">문자내용</span>'
                  + `<button class="hd-msg-tog" data-hidx="${idx}" style="font-size:13px;padding:4px 14px;border:1px solid #ccc;border-radius:6px;background:#fff;cursor:pointer">펼치기</button>`
                  + '</div>'
                  + `<div class="hd-msg" data-hidx="${idx}" style="font-size:12px;color:#333;background:#fff;border:1px solid #eee;border-radius:6px;padding:8px 10px;white-space:pre-wrap;word-break:break-word;line-height:1.6;display:none">${escapeHtml(rec.message)}</div>`
                  + '</div>';
        }
        if (rec.internal.length) {
            html += '<div style="font-size:12px;font-weight:700;color:#1D4ED8;margin-bottom:6px">내부 (' + rec.internal.length + ')</div>';
            html += rec.internal.map((r, j) => renderPerson(r, j, true)).join('');
        }
        if (rec.external.length) {
            html += `<div style="font-size:12px;font-weight:700;color:#7C3AED;margin:${rec.internal.length ? '10px' : '0'} 0 6px 0">외부 (${rec.external.length})</div>`;
            html += rec.external.map((r, j) => renderPerson(r, j, false)).join('');
        }
        html += '</div>';
        return html;
    }

    renderHistoryTable() {
        const { historyFilter: filter, historySearch: search } = this.state;
        this.historyFiltered = this.filterHistory();

        // 필터 바 + 빈 테이블
        $('#tA').html(`
            <div class="hf">
                <span style="font-size:14px;font-weight:600;color:#DC2626">발송일시</span>
                <button class="hf-pill${filter === 'all'   ? ' on' : ''}" data-hf="all">전체</button>
                <button class="hf-pill${filter === 'today' ? ' on' : ''}" data-hf="today">오늘</button>
                <button class="hf-pill${filter === 'week'  ? ' on' : ''}" data-hf="week">1주일</button>
                <span style="color:#ccc;margin:0 4px">|</span>
                <input class="hf-input" id="hSrch" placeholder="수신자 검색" value="${search}">
                <button class="hf-btn hf-bs" id="hSBtn">검색</button>
                <button class="hf-btn hf-br" id="hRBtn">초기화</button>
            </div>
            <table class="ht"><thead><tr>
                <th>발송일시</th><th>발송자(ID)</th><th>발송</th><th>문자유형</th><th>결과</th>
            </tr></thead></table>`);

        this.historyDt = initDataTable($('#tA > table').first(), {
            data: this.historyFiltered,
            columns: [
                { data: 'dateTime',
                    render: (d, _, __, meta) => `<span style="font-weight:600;white-space:nowrap"><button class="ht-ex" data-hidx="${meta.row}" style="margin-right:8px">펼치기</button>${d}</span>` },
                { data: null,
                    render: r => `${r.admin} <span style="color:#909090;font-size:12px">(${r.adminId})</span>` },
                { data: null, className: 'text-center',
                    render: r => {
                        // 발송 종류 = detail_purpose. 한 배치 = 한 종류 → 단일 태그.
                        const KIND = {
                            internal:          ['내부',         '#EFF6FF', '#1D4ED8'],
                            external:          ['외부·배차문의', '#F5F3FF', '#7C3AED'],
                            external_dispatch: ['외부·현장정보', '#F5F3FF', '#7C3AED'],
                        };
                        const [lbl, bg, fg] = KIND[r.kind] || ['발송', '#F1F5F9', '#64748B'];
                        const cnt = r.internal.length + r.external.length;
                        return `<span style="padding:2px 8px;border-radius:var(--adm-radius-chip, 999px);background:${bg};color:${fg};font-size:12px;font-weight:600">${lbl} ${cnt}명</span>`;
                    } },
                { data: null, className: 'text-center',
                    render: r => {
                        // 한 배치는 동일한 msg_type 으로 발송됨. 대표 1건의 타입을 표시.
                        const all = [...r.internal, ...r.external];
                        return msgTypeBadge(all.length ? all[0].messageType : '');
                    } },
                { data: null,
                    render: r => {
                        const all = [...r.internal, ...r.external];
                        const allFail = all.every(x => x.result === '실패');
                        const hasFail = all.some(x => x.result === '실패');
                        const result = allFail ? '실패' : hasFail ? '일부실패' : '성공';
                        const color  = result === '성공' ? '#15803D' : result === '일부실패' ? '#D97706' : '#DC2626';
                        return `<span style="font-weight:700;color:${color}">${result}</span>`;
                    } },
            ],
            rowCallback: (row, _, idx) => $(row).attr('data-hidx', idx),
            order: [[ 0, 'desc' ]],   // 발송일시 내림차순
            info:  false,             // "총 N개" 정보바 숨김
        });
    }

    toggleHistoryRow($btn) {
        if (!this.historyDt) return;
        const $tr = $btn.closest('tr');
        const row = this.historyDt.row($tr);
        const idx = +$btn.data('hidx');
        const rec = this.historyFiltered[idx];
        if (!rec) return;
        if (row.child.isShown()) {
            row.child.hide();
            $btn.text('펼치기');
        } else {
            row.child(this.renderHistoryDetail(rec, idx)).show();
            $btn.text('접기');
        }
    }

    resendHistory(idx, type, ri) {
        const rec = this.sendHistory[idx];
        if (!rec) return;
        const arr = type === 'internal' ? rec.internal : rec.external;
        if (arr[ri]) {
            arr[ri].result = '성공';
            this.renderHistoryTable();
        }
    }

    // ============================================================
    //  메인 렌더
    // ============================================================
    updateTabCounter(selector, count, active, activeColor) {
        $(selector).text(count).css({
            background: active ? activeColor : '#ddd',
            color:      active ? '#fff'      : '#909090',
        });
    }

    updateSendButton(total) {
        const $btn = $('#bSM');
        if (total > 0) $btn.attr('class', 'bsn bsn-send on').prop('disabled', false).text(`문자발송 (${total})`);
        else           $btn.attr('class', 'bsn bsn-send off').prop('disabled', true).text('문자발송');
    }

    setTabClasses() {
        // 외부배차 오더 — 외부 탭은 숨기고, 내부매칭 탭(배차자 + 매칭리스트)은 '외부매칭' 으로 라벨 변경
        if (this.isExtDispatch) {
            $('.t4[data-tab="external"]').hide();
            $('#tIL').text('외부매칭');
        }

        const map = { internal: 'a1', external: 'a2', response: 'a3', history: 'a4' };
        const activeTab = this.state.activeTab;
        $('.t4').each(function () {
            const tab = $(this).data('tab');
            const cls = tab === activeTab ? `t4 ${map[tab]}` : 't4';
            $(this).attr('class', cls);
        });
    }

    render() {
        const { state } = this;

        // ── 발송내역 모드 — 발송 가능 여부와 무관하게 발송이력만 표시 ──
        if (state.historyMode) {
            $('#nB').hide();
            $('#sB').hide();
            $('#hB').css('display', 'flex');
            $('#sP').hide();
            $('#smWrap').hide();
            this.renderHistoryTable();
            return;
        }

        const intCount = state.checkedInternal.size;
        const extCount = state.checkedExternal.size;
        const total    = intCount + extCount;

        $('#hB').hide();
        $('#nB').css('display', state.sendMode ? 'none' : 'flex');
        $('#sB').css('display', state.sendMode ? 'flex' : 'none');
        $('#sP').toggle(state.sendMode && state.showSettings);
        $('#bSt').toggleClass('ac', state.showSettings);
        $('#bHB').text(`발송내역 (${this.sendHistory.length})`);

        const bizLabel = EQUIPMENT_TYPES.find(t => t.key === this.ORDER.equipmentKey)?.label || '';
        $('#bDB').text(`${bizLabel} ${kmLabel(state.distanceConfig[this.ORDER.equipmentKey], this.bucketsFor(this.ORDER.equipmentKey))}`);

        $('#bBDE').text(state.showBizDistEditor ? '닫기' : '설정').toggleClass('ay', state.showBizDistEditor);
        $('#bdEA').toggle(state.showBizDistEditor);
        this.renderEquipmentGrid('#bgS');
        this.renderBizDistEditor('#brS');

        const inactiveMax = maxValue([...state.inactiveConfigPeriods]);
        const inactiveLbl = INACTIVE_PERIODS.find(p => p.value === inactiveMax);
        // "N일 이내" 카운트 — 설정된 최대 범위 안에 들어오는 회원 수
        const withinCnt = inactiveMax > 0
            ? this.internalMembers.filter(m => daysSince(m.lastAccess) <= inactiveMax).length
            : 0;
        $('#iB').text(inactiveLbl ? inactiveLbl.label : inactiveMax + '일');
        $('#iCT').text(`${withinCnt}명 해당`);

        $('#bIE').text(state.showInactiveEditor ? '닫기' : '설정').toggleClass('ar', state.showInactiveEditor);
        $('#iEA').toggle(state.showInactiveEditor);
        this.renderInactiveConfigPills();

        // 내부/외부 탭은 상태와 무관하게 항상 보인다 — 필터·발송버튼(#smFlex)만 발송모드 전용
        $('#smWrap').css('display', 'block');
        this.setTabClasses();
        // 탭 카운터 — 발송모드: 선택 인원 / 평상시: 목록 인원
        // 외부배차 오더는 발송대상(배차자 1명)이 checkedExternal 에 담기므로 외부매칭 탭 카운터도 그 수를 표시 (checkedInternal=0)
        const intTabCnt = state.sendMode ? (this.isExtDispatch ? extCount : intCount) : this.internalMembers.length;
        const extTabCnt = state.sendMode ? extCount : this.externalAgencies.length;
        this.updateTabCounter('#tIC', intTabCnt, state.activeTab === 'internal', '#1D4ED8');
        this.updateTabCounter('#tEC', extTabCnt, state.activeTab === 'external', '#7C3AED');
        this.updateTabCounter('#tRC', this.responseList.length,state.activeTab === 'response', '#15803D');
        this.updateTabCounter('#tHC', this.sendHistory.length, state.activeTab === 'history',  '#DC2626');
        this.renderLegend();
        this.updateSendButton(total);

        // 외부배차는 발송모드에서 지정자 1명만 노출 → 거리/미접속 필터 숨김(필터로 숨겨진 내부회원 선택·발송 방지)
        const showFilter = state.sendMode && !this.isExtDispatch
            && (state.activeTab === 'internal' || state.activeTab === 'external');
        $('#fBar').css('display', showFilter ? 'flex' : 'none');
        $('#smFlex').css('display', state.sendMode ? 'flex' : 'none');
        // 발송이력 탭에서는 문자발송 버튼 숨김 (이미 보낸 내역 조회 화면이라 무의미)
        $('#smRight').css('display', state.sendMode && state.activeTab !== 'history' ? 'flex' : 'none');
        if (showFilter) { this.renderDistancePills(); this.renderInactiveBar(); }

        switch (state.activeTab) {
            case 'internal': this.tables.renderInternal(); break;
            case 'external': this.tables.renderExternal(); break;
            case 'response': this.tables.renderResponse(); break;
            case 'history':  this.renderHistoryTable();    break;
        }
    }

    // ============================================================
    //  이벤트 바인딩
    // ============================================================
    bindEvents() {
        const self = this;
        const { state, smsState } = this;

        // 모드 토글 — 버튼이 수주대기일 때만 렌더되므로 존재 여부 확인
        $('#bES').on('click', () => self.enterSendMode());
        $('#bEX').on('click', () => self.exitSendMode());
        // 발송내역 보기 진입/닫기 — 항상 노출되는 버튼 (수주대기 아니어도 사용 가능)
        $('#bHB').on('click', () => self.enterHistoryMode());
        $('#hBX').on('click', () => self.exitHistoryMode());
        // 이름/회원번호 링크는 inline `javascript:$j.F.Admin.Util.*` 로 처리 (matchList 패턴 동일)

        // 탭 — 전환 시 체크 선택 초기화 (내부·외부 따로 누적되지 않도록)
        $(document).on('click.mlsms','#msx-root .t4', function () {
            const nextTab = $(this).data('tab');
            if (nextTab !== state.activeTab) {
                state.checkedInternal = new Set();
                state.checkedExternal = new Set();
                state.selectedIntDist = new Set();
                state.selectedExtDist = new Set();
                state.selectedAccess = new Set();
                state.selectedPaid = new Set();
            }
            state.activeTab = nextTab;
            self.render();
        });

        // 설정 토글
        $(document).on('click.mlsms','#bBDE', () => { state.showBizDistEditor   = !state.showBizDistEditor;   self.render(); });
        $(document).on('click.mlsms','#bIE',  () => { state.showInactiveEditor  = !state.showInactiveEditor;  self.render(); });

        // 거리 pill 토글 — 거리·미접속은 교집합으로 적용 (applyFilters)
        // 아무것도 안 고른 상태에서 중간 구간을 바로 누르면, 그 이하 구간도 함께 선택된다
        // (처음부터 3·5·10km 를 하나씩 눌러 채우지 않아도 되게). 이미 하나라도 골라둔 뒤에는
        // 그 pill 하나만 토글 — 세밀 조정을 막지 않는다.
        $(document).on('click.mlsms','#msx-root .dp', function () {
            const km = +$(this).data('km');
            const isInternal = state.activeTab === 'internal';
            const target = isInternal ? state.selectedIntDist : state.selectedExtDist;

            if (target.size === 0) {
                self.currentBuckets().filter(b => b <= km).forEach(b => target.add(b));
            } else {
                target.has(km) ? target.delete(km) : target.add(km);
            }
            if (isInternal) self.applyFilters();
            else            self.applyExtFilters();
            self.render();
        });

        $(document).on('click.mlsms','#saToggle', () => {
            const isInternal = state.activeTab === 'internal';
            const target = isInternal ? state.selectedIntDist : state.selectedExtDist;
            if (target.size > 0) {
                if (isInternal) { state.selectedIntDist = new Set(); state.selectedAccess = new Set(); state.selectedPaid = new Set(); }
                else            { state.selectedExtDist = new Set(); state.selectedExtWorkPeriods = new Set(); }
            } else {
                self.currentBuckets().forEach(k => target.add(k));
            }
            if (isInternal) self.applyFilters();
            else            self.applyExtFilters();
            self.render();
        });

        // 접속 pill 토글 (접속/미접속) — 거리와 교집합
        $(document).on('click.mlsms','#msx-root .ip', function () {
            const v = $(this).data('iip');   // 'online' | 'offline'
            state.selectedAccess.has(v) ? state.selectedAccess.delete(v) : state.selectedAccess.add(v);
            self.applyFilters();
            self.render();
        });
        $(document).on('click.mlsms','#iToggle', () => {
            state.selectedAccess = state.selectedAccess.size > 0
                ? new Set()
                : new Set(ACCESS_GROUPS.map(g => g.value));
            self.applyFilters();
            self.render();
        });

        // 유료/무료 pill 토글 — 거리·접속과 교집합
        // 클래스는 .mp — 모달 패널(.pp)과 겹치면 모달 클릭이 필터로 새어들어간다
        $(document).on('click.mlsms','#msx-root .mp', function () {
            const v = $(this).data('pp');   // 'paid' | 'free'
            state.selectedPaid.has(v) ? state.selectedPaid.delete(v) : state.selectedPaid.add(v);
            self.applyFilters();
            self.render();
        });
        $(document).on('click.mlsms','#pToggle', () => {
            state.selectedPaid = state.selectedPaid.size > 0
                ? new Set()
                : new Set(PAID_GROUPS.map(g => g.value));
            self.applyFilters();
            self.render();
        });

        // 외부배차 최근오더일 pill 토글 — 거리와 교집합
        $(document).on('click.mlsms','#msx-root .wp', function () {
            const v = +$(this).data('wp');
            state.selectedExtWorkPeriods.has(v) ? state.selectedExtWorkPeriods.delete(v) : state.selectedExtWorkPeriods.add(v);
            self.applyExtFilters();
            self.render();
        });
        $(document).on('click.mlsms','#wToggle', () => {
            state.selectedExtWorkPeriods = state.selectedExtWorkPeriods.size > 0
                ? new Set()
                : new Set(WORK_PERIODS.map(p => p.value));
            self.applyExtFilters();
            self.render();
        });

        // 업종별 거리 에디터
        $(document).on('change.mlsms','#msx-root .bsel', function () {
            state.editingBiz = $(this).val();
            state.editingKm  = state.editingBiz ? state.distanceConfig[state.editingBiz] : null;
            self.render();
        });
        $(document).on('click.mlsms','#msx-root [data-bkm]', function () {
            if (!state.editingBiz) return;
            const km = +$(this).data('bkm');
            const opts = self.bucketsFor(state.editingBiz);
            const cur = state.editingKm || 0;
            if (km > cur) {
                state.editingKm = km;
            } else if (km === cur) {
                const below = opts.filter(d => d < km);
                state.editingKm = below.length ? Math.max(...below) : null;
            } else {
                const below = opts.filter(d => d < km);
                state.editingKm = below.length ? Math.max(...below) : null;
            }
            self.render();
        });
        $(document).on('click.mlsms','#msx-root .bc2', () => {
            if (!state.editingBiz || state.editingKm == null) return;
            state.distanceConfig[state.editingBiz] = state.editingKm;
            state.editingBiz = '';
            state.editingKm  = null;
            self.render();
        });

        // 미접속기간 설정 (발송설정 패널)
        $(document).on('click.mlsms','#msx-root [data-iv]', function () {
            self.toggleInactiveDay(+$(this).data('iv'));
        });

        // 전체 선택 체크박스 — 수발주제한 회원은 제외 (개별 클릭으로만 선택 가능)
        $(document).on('click.mlsms','#iSA', function (e) {
            e.stopPropagation();
            const sendable = self.internalMembers.filter(m => !isRestricted(m));
            const allSendableChecked = sendable.length > 0
                && sendable.every(m => state.checkedInternal.has(m.no));
            state.checkedInternal = allSendableChecked
                ? new Set()
                : new Set(sendable.map(m => m.no));
            self.render();
        });
        $(document).on('click.mlsms','#eSA', function (e) {
            e.stopPropagation();
            const sendable = self.externalListSendable();
            state.checkedExternal = state.checkedExternal.size === sendable.length
                ? new Set()
                : new Set(sendable.map(e2 => e2.id));
            self.render();
        });

        // 행 체크박스
        $(document).on('click.mlsms','#msx-root .ic2', function (e) {
            e.stopPropagation();
            const no = +$(this).data('no');
            state.checkedInternal.has(no) ? state.checkedInternal.delete(no) : state.checkedInternal.add(no);
            self.render();
        });
        $(document).on('click.mlsms','#msx-root .ec2', function (e) {
            e.stopPropagation();
            const id = +$(this).data('id');
            state.checkedExternal.has(id) ? state.checkedExternal.delete(id) : state.checkedExternal.add(id);
            self.render();
        });

        // 행 클릭으로도 토글
        $(document).on('click.mlsms','#msx-root tr[data-mn]', function (e) {
            if ($(e.target).is('a, input')) return;
            if (!state.sendMode) return;
            const no = +$(this).data('mn');
            state.checkedInternal.has(no) ? state.checkedInternal.delete(no) : state.checkedInternal.add(no);
            self.render();
        });
        $(document).on('click.mlsms','#msx-root tr[data-ei]', function (e) {
            if ($(e.target).is('input, button')) return;
            if (!state.sendMode) return;
            const id = +$(this).data('ei');
            const agency = self.externalListFiltered().find(x => x.id === id);
            if (!agency || !agency.notifyOk) return;
            state.checkedExternal.has(id) ? state.checkedExternal.delete(id) : state.checkedExternal.add(id);
            self.render();
        });

        // 외부배차 지정 — 평상시 외부 탭 각 행의 [지정] 버튼
        $(document).on('click.mlsms','#msx-root .ext-assign-btn', function (e) {
            e.stopPropagation();
            const id = +$(this).data('id');
            const agency = self.externalListFiltered().find(x => x.id === id);
            if (agency) self.assignExternal(agency);
        });

        // 스펙일치 필터 / 최종접속 정렬
        $(document).on('click.mlsms','#sA',  () => {
            const cur = state.sortByInactive;
            state.sortByInactive = cur === '' ? 'oldest' : (cur === 'oldest' ? 'newest' : '');
            self.render();
        });

        // 수주응답 — 배차확정/취소
        $(document).on('click.mlsms','#msx-root .conf-btn', function (e) {
            e.stopPropagation();
            const ri = +$(this).data('ri');
            const { name } = self.responseList[ri];
            setTimeout(() => {
                if (confirm(`${name} 배차확정 하시겠습니까?`)) { self.responseList[ri].status = '배차확정'; self.render(); }
            }, 50);
        });
        $(document).on('click.mlsms','#msx-root .conf-cancel', function (e) {
            e.stopPropagation();
            const ri = +$(this).data('ri');
            const { name } = self.responseList[ri];
            setTimeout(() => {
                if (confirm(`${name} 배차를 취소하시겠습니까?`)) { self.responseList[ri].status = '대기'; self.render(); }
            }, 50);
        });

        // 발송이력 필터
        $(document).on('click.mlsms','#msx-root .hf-pill', function () { state.historyFilter = $(this).data('hf'); self.render(); });
        $(document).on('click.mlsms','#hSBtn', () => { state.historySearch = $('#hSrch').val().trim(); self.render(); });
        $(document).on('click.mlsms','#hRBtn', () => { state.historyFilter = 'all'; state.historySearch = ''; self.render(); });
        $(document).on('keydown.mlsms','#hSrch', function (e) {
            if (e.key === 'Enter') { state.historySearch = $(this).val().trim(); self.render(); }
        });

        // 이력 펼치기/접기/재발송
        $(document).on('click.mlsms','#msx-root .ht-ex', function () { self.toggleHistoryRow($(this)); });
        $(document).on('click.mlsms','#msx-root .hd-resend', function (e) {
            e.stopPropagation();
            self.resendHistory(+$(this).data('hidx'), $(this).data('htype'), +$(this).data('hri'));
        });
        // 문자내용 접기/펼치기
        $(document).on('click.mlsms','#msx-root .hd-msg-tog', function () {
            const $btn = $(this);
            const $msg = $btn.parent().next('.hd-msg');
            const shown = $msg.is(':visible');
            $msg.toggle(!shown);
            $btn.text(shown ? '펼치기' : '접기');
        });

        // 발송 모달 열기 / 모달 외부 클릭 닫기
        //   — 수발주제한 회원이 선택돼 있으면 swalert 로 한 번 확인 후 진행
        // 열 때마다 기본값으로 — 닫기 전 편집하던 본문·발송번호는 이어받지 않는다
        const openSendModal = () => {
            smsState.smsIntText  = '';
            smsState.smsExtText  = '';
            smsState.showPreview = false;
            smsState.senderIdx   = 0;
            self.sendModal.render();
            $('#sO').css('display', 'flex');
        };
        $('#bSM').on('click', () => {
            const restricted = self.internalMembers.filter(m =>
                state.checkedInternal.has(m.no) && isRestricted(m)
            );
            if (restricted.length > 0) {
                const lines = restricted.map(m => {
                    const tags = [];
                    if (m.restrictions.takeBlocked) tags.push('수주제한');
                    if (m.restrictions.giveBlocked) tags.push('발주제한');
                    return `· ${m.name} [${m.no}] (${tags.join('/')})`;
                }).join('\n');
                const total = state.checkedInternal.size;
                Util.swalert(
                    [
                        '수발주제한 회원 포함',
                        'warning',
                        `선택된 ${total}명 중 ${restricted.length}명이 수발주제한 상태입니다:\n${lines}\n\n그대로 진행하시겠습니까?`,
                    ],
                    openSendModal,
                    ['그대로 진행', '취소']
                );
                return;
            }
            openSendModal();
        });

        $(document).on('click.mlsms','#msx-root .ov', function (e) {
            if ($(e.target).hasClass('ov')) $(this).hide();
        });
    }

    /**
     * 외부배차 지정 — [지정] 버튼이 부른다. 오더관리의 [외부배차전환]과 같은 길이다
     * (`/adm/ajax/order/{ordernum}/sendOther` → OrderDispatchService::sendOtherByAdmin).
     * 대상 판정(수주 여부·홀드 잔여)은 서버가 한다 — 화면의 DATA 는 연 시점 스냅샷이라 미리 막지 않는다.
     * 되면 새로고침 — 외부 탭이 배차된 1명(상단 빨간 행)으로 바뀐다.
     * @param {object} agency externalAgencies 의 한 행 (id·company·phone·distance …)
     */
    assignExternal(agency) {
        const orderNum = this.DATA.order?.num || 0;
        if (!orderNum) return;

        // 이름만 고칠 수 있다 — 외부회원 표에는 상호와 기사 이름이 한 칸이라 상호가 그대로 들어가면
        // 안 될 때가 있다. 이름은 이 오더의 수주자 표시에만 들어가고 외부회원 표는 덮어쓰지 않는다.
        // **전화는 못 고친다** — 외부회원과 오더를 잇는 것이 전화번호 하나다(오더 driver_phone ↔
        // g5_out_members.phone, 최근 작업일도 이 번호로 갱신). 바꾸면 고른 외부회원과 끊어진다
        const esc  = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
        const name = () => String($('#ml-ext-name').val() || '').trim();

        $j.Ui.Dialog.open({
            tone:    'warn',
            title:   '외부배차 지정',
            desc:    '이 오더를 아래 작업자에게 배차(임의발주)하고 곧바로 작업완료로 처리합니다.',
            html:    `<div class="ml-ext-form">
                <label for="ml-ext-name">작업자 이름</label>
                <input id="ml-ext-name" class="ui-dlg-input" type="text" value="${esc(agency.company)}">
                <label for="ml-ext-phone">작업자 전화</label>
                <input id="ml-ext-phone" class="ui-dlg-input" type="tel" value="${esc(Util.phoneNumber(agency.phone || ''))}" readonly>
            </div>`,
            confirm: '외부배차',
            cancel:  '닫기',
            validate: () => name() ? '' : '작업자 이름을 입력해 주세요.',
            onOpen:  () => { const el = document.getElementById('ml-ext-name'); if (el) el.select(); },
            onConfirm: () => {
                $.ajax({
                    type: 'POST',
                    url:  `/adm/ajax/order/${orderNum}/sendOther`,
                    data: { typech_workerName: name(), typech_workerPhone: agency.phone },
                    success: (data) => {
                        if (!data || data.result === false) {
                            Util.swalert([(data && data.message) || '처리하지 못했습니다.', 'warning']);
                            return;
                        }
                        Util.swalert([data.message || '외부배차로 전환했습니다.', 'success'], () => location.reload());
                    },
                });
            },
        });
    }

    /**
     * $j.destroy('Pages.Admin.MatchList') 호출 시 정리.
     * 페이지 전환·HMR 시 document 에 잔존하는 핸들러 누수 방지.
     */
    destroy() {
        $(document).off('.mlsms');
        if (this.tables && typeof this.tables.destroy === 'function') this.tables.destroy();
    }
}